import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";

export interface Track {
  id: string;
  title: string;
  artist: string;
  artistId?: string;
  album?: string;
  duration: number;
  coverImage?: string;
  audioUrl: string;
  genre?: string;
  plays?: number;
  likes?: number;
  createdAt?: string;
  source?: "LOCAL" | "MIXCLOUD" | "BEATPORT" | "BANDCAMP";
}

interface PlayerContextType {
  currentTrack: Track | null;
  isPlaying: boolean;
  progress: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  isLoading: boolean;
  queue: Track[];
  playTrack: (track: Track) => void;
  pauseTrack: () => void;
  resumeTrack: () => void;
  nextTrack: () => void;
  previousTrack: () => void;
  seekTo: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  addToQueue: (track: Track) => void;
  removeFromQueue: (index: number) => void;
  clearQueue: () => void;
  setProgress: (progress: number) => void;
  setDuration: (duration: number) => void;
  setExternalControls: (controls: any) => void;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error("usePlayer must be used within a PlayerProvider");
  }
  return context;
};

export const PlayerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [queue, setQueue] = useState<Track[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const externalControlsRef = useRef<any>(null);

  const setExternalControls = useCallback((controls: any) => {
    externalControlsRef.current = controls;
  }, []);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = volume;
    }

    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      setProgress(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };

    const handleEnded = () => {
      nextTrack();
    };

    const handleWaiting = () => {
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("canplay", handleCanPlay);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("canplay", handleCanPlay);
    };
  }, []);

  const playTrack = useCallback((track: Track) => {
    if (!audioRef.current) return;

    setCurrentTrack(track);
    setIsLoading(true);

    if (track.source && track.source !== "LOCAL") {
      // For external sources, pause the native player completely
      audioRef.current.pause();
      audioRef.current.src = "";
      setIsPlaying(true);
      return;
    }

    audioRef.current.src = track.audioUrl;
    audioRef.current
      .play()
      .then(() => {
        setIsPlaying(true);
      })
      .catch((err) => {
        console.error("Error playing track:", err);
        setIsLoading(false);
      });
  }, []);

  const pauseTrack = useCallback(() => {
    if (currentTrack?.source && currentTrack.source !== "LOCAL") {
      setIsPlaying(false);
      if (externalControlsRef.current) externalControlsRef.current.pause();
      return;
    }
    if (!audioRef.current) return;
    audioRef.current.pause();
    setIsPlaying(false);
  }, [currentTrack]);

  const resumeTrack = useCallback(() => {
    if (currentTrack?.source && currentTrack.source !== "LOCAL") {
      setIsPlaying(true);
      if (externalControlsRef.current) externalControlsRef.current.play();
      return;
    }
    if (!audioRef.current) return;
    audioRef.current
      .play()
      .then(() => {
        setIsPlaying(true);
      })
      .catch((err) => {
        console.error("Error resuming track:", err);
      });
  }, [currentTrack]);

  const nextTrack = useCallback(() => {
    if (queue.length === 0 || currentIndex >= queue.length - 1) return;
    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
    playTrack(queue[nextIndex]);
  }, [queue, currentIndex, playTrack]);

  const previousTrack = useCallback(() => {
    if (!audioRef.current) return;
    if (audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      setCurrentIndex(prevIndex);
      playTrack(queue[prevIndex]);
    }
  }, [queue, currentIndex, playTrack]);

  const seekTo = useCallback(
    (time: number) => {
      if (currentTrack?.source && currentTrack.source !== "LOCAL") {
        if (externalControlsRef.current)
          externalControlsRef.current.seekTo(time);
        setProgress(time);
        return;
      }
      if (!audioRef.current) return;
      audioRef.current.currentTime = time;
      setProgress(time);
    },
    [currentTrack],
  );

  const setVolume = useCallback((newVolume: number) => {
    if (!audioRef.current) return;
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    audioRef.current.volume = clampedVolume;
    setVolumeState(clampedVolume);
    if (clampedVolume > 0) {
      setIsMuted(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.volume = volume || 0.8;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  const addToQueue = useCallback((track: Track) => {
    setQueue((prev) => [...prev, track]);
  }, []);

  const removeFromQueue = useCallback((index: number) => {
    setQueue((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
    setCurrentIndex(-1);
  }, []);

  return (
    <PlayerContext.Provider
      value={{
        currentTrack,
        isPlaying,
        progress,
        duration,
        volume,
        isMuted,
        isLoading,
        queue,
        playTrack,
        pauseTrack,
        resumeTrack,
        nextTrack,
        previousTrack,
        seekTo,
        setVolume,
        toggleMute,
        addToQueue,
        removeFromQueue,
        clearQueue,
        setProgress,
        setDuration,
        setExternalControls,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};
